"use client"

import React, { useEffect, useRef, useState } from "react"
import { motion, useInView, animate, useReducedMotion } from "motion/react"
import type { StatsBlock as StatsBlockProps } from "@/payload-types"
import { cn } from "@/utilities/ui"
import * as Icons from "lucide-react"

const easeOutExpo = [0.16, 1, 0.3, 1] as [number, number, number, number]

const AnimatedCounter: React.FC<{ value: string }> = ({ value }) => {
	const ref = useRef(null)
	const isInView = useInView(ref, { once: true, amount: 0.5 })
	const shouldReduceMotion = useReducedMotion()
	const [displayValue, setDisplayValue] = useState("0")

	// Extract number and suffix/prefix
	const numberMatch = value.match(/(\d+([,.]\d+)*)/)
	const numberPart = numberMatch ? numberMatch[0].replace(/,/g, "") : "0"
	const prefix = value.split(numberMatch ? numberMatch[0] : "")[0] || ""
	const suffix = value.split(numberMatch ? numberMatch[0] : "")[1] || ""
	const targetNumber = parseFloat(numberPart)
	const isInteger = !numberPart.includes(".")
	const hasCommas = numberMatch ? numberMatch[0].includes(",") : false

	useEffect(() => {
		if (isInView) {
			if (shouldReduceMotion) {
				setDisplayValue(value)
				return
			}

			const controls = animate(0, targetNumber, {
				duration: 1.5,
				ease: easeOutExpo,
				onUpdate: (latest) => {
					let formatted = isInteger
						? Math.floor(latest).toString()
						: latest.toFixed(1)

					// Add commas if original had them
					if (hasCommas) {
						formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
					}

					setDisplayValue(`${prefix}${formatted}${suffix}`)
				},
			})
			return () => controls.stop()
		}
	}, [
		isInView,
		targetNumber,
		prefix,
		suffix,
		isInteger,
		hasCommas,
		shouldReduceMotion,
		value,
	])

	return (
		<span ref={ref} className="tabular-nums" role="img" aria-label={value}>
			<span aria-hidden="true">{displayValue}</span>
		</span>
	)
}

export const StatsBlock: React.FC<StatsBlockProps> = ({
	title,
	items,
	layout = "grid-4",
	style = "cards",
}) => {
	if (!items || items.length === 0) return null

	const gridCols = {
		"grid-2": "grid-cols-1 md:grid-cols-2",
		"grid-3": "grid-cols-1 md:grid-cols-3",
		"grid-4": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
		"grid-5": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5",
	}

	return (
		<section className="py-20 bg-white">
			<div className="container px-4 mx-auto">
				{title && (
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.8, ease: easeOutExpo }}
						className="text-3xl md:text-4xl font-bold text-center mb-16 tracking-tight text-slate-900"
					>
						{title}
					</motion.h2>
				)}

				<div className={cn("grid gap-8", gridCols[layout || "grid-4"])}>
					{items.map((item, index) => {
						const IconComponent = item.icon
							? (Icons as unknown as Record<string, React.ElementType>)[
									item.icon
								] || null
							: null

						return (
							<motion.div
								key={item.id || index}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 1.2,
									delay: index * 0.1,
									ease: easeOutExpo,
								}}
								className={cn(
									"flex flex-col items-center text-center transition-all duration-300",
									style === "cards" &&
										"p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1",
									style === "minimal" && "p-4",
									style === "with-icon" && "p-6",
								)}
							>
								{style === "with-icon" && IconComponent && (
									<div className="mb-6 p-4 rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-200">
										<IconComponent size={32} strokeWidth={1.5} />
									</div>
								)}

								<div
									className={cn(
										"font-bold tracking-tighter text-slate-900 mb-2",
										style === "minimal"
											? "text-5xl md:text-6xl"
											: "text-4xl md:text-5xl",
									)}
								>
									<AnimatedCounter value={item.value} />
								</div>

								<div className="text-slate-700 font-medium text-lg uppercase tracking-widest text-sm">
									{item.label}
								</div>

								{style !== "with-icon" && IconComponent && (
									<div className="mt-4 text-slate-300">
										<IconComponent size={24} strokeWidth={1} />
									</div>
								)}
							</motion.div>
						)
					})}
				</div>
			</div>
		</section>
	)
}
